import React, { useState } from 'react';
import { Typography, TextField, Button, Stack, Alert, Link as MuiLink, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { FieldGrid } from '../components/ui';
import { tokens } from '../theme';
import { AuthLayout, PasswordToggle } from '../components/auth/AuthLayout';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    rodoConsent: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }

    if (!formData.rodoConsent) {
      setError('Musisz wyrazić zgodę na przetwarzanie danych osobowych');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      login(result.accessToken, {
        id: result.user.id,
        email: result.user.email,
        role: result.user.role as 'SUPER_ADMIN' | 'SALON_OWNER' | 'STAFF' | 'CLIENT',
      });
      navigate('/dashboard');
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Wystąpił błąd rejestracji');
    } finally {
      setLoading(false);
    }
  };

  const passwordsDiffer = formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword;

  return (
    <AuthLayout title="Załóż konto" description="Kilka danych i możesz zacząć korzystać z Visitio.">
      <form onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
          <FieldGrid columns={2}>
            <TextField
              label="Imię"
              fullWidth
              value={formData.firstName}
              onChange={handleChange('firstName')}
              required
              autoComplete="given-name"
            />
            <TextField
              label="Nazwisko"
              fullWidth
              value={formData.lastName}
              onChange={handleChange('lastName')}
              required
              autoComplete="family-name"
            />
          </FieldGrid>
          <TextField
            label="E-mail"
            type="email"
            fullWidth
            value={formData.email}
            onChange={handleChange('email')}
            required
            autoComplete="email"
            placeholder="nazwa@salon.pl"
          />
          <TextField
            label="Hasło"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={formData.password}
            onChange={handleChange('password')}
            required
            autoComplete="new-password"
            helperText="Co najmniej 8 znaków, w tym wielka litera i cyfra"
            InputProps={{ endAdornment: <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} /> }}
          />
          <TextField
            label="Powtórz hasło"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            required
            autoComplete="new-password"
            error={passwordsDiffer}
            helperText={passwordsDiffer ? 'Hasła nie są takie same' : undefined}
          />
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mx: 0, gap: 1 }}
            control={
              <Checkbox
                checked={formData.rodoConsent}
                onChange={handleChange('rodoConsent')}
                sx={{ p: 0, mt: '1px' }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: tokens.inkSoft }}>
                Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z polityką prywatności (RODO).
              </Typography>
            }
          />
          <Button variant="contained" size="large" fullWidth type="submit" disabled={loading} sx={{ mt: 1 }}>
            {loading ? 'Zakładanie konta…' : 'Załóż konto'}
          </Button>
        </Stack>
      </form>
      <Typography variant="body2" sx={{ mt: 3, pt: 3, borderTop: `1px solid ${tokens.line}`, color: tokens.inkSoft }}>
        Masz już konto?{' '}
        <MuiLink component={Link} to="/login">Zaloguj się</MuiLink>
      </Typography>
    </AuthLayout>
  );
};
